/**
 * Thin wrapper around TipTap (ProseMirror) — the rich derivative editor.
 *
 * Supersedes the Vditor wrapper. Vditor round-tripped markdown perfectly well,
 * but has no concept of a custom inline node, so a `[[src:...]]` citation
 * rendered as literal text inside it and a comment could not be anchored to a
 * text range at all. Citation node views, range-anchored comments and
 * track-changes are all ProseMirror primitives; that is the whole reason for
 * the swap.
 *
 * The schema and the markdown contract live in {@link ./tiptapMarkdown}, shared
 * with the round-trip guard so the editor and the test can never disagree about
 * what a document serializes to. This file holds only the React lifecycle.
 *
 * Coverage-excluded, on the ADR-023 precedent: TipTap mounts a real
 * contenteditable surface that cannot be meaningfully driven or asserted under
 * jsdom. Note the exclude is narrower than Vditor's was — the schema and
 * serialization logic moved into `tiptapMarkdown.ts`, which IS fully covered
 * (headless, no DOM mount). Only the lifecycle glue is unverified here, and
 * `e2e/rich-editor.spec.ts` guards it in a real browser (ADR-028). No content is logged
 * (HIPAA).
 *
 * **Selection-native commenting (ADR-063, BLA-319):** a text selection can be
 * commented on via three equivalent routes: the ribbon Insert dropdown ("Add Comment"),
 * a right-click on the selection (a small menu, mirroring Google Docs), or the
 * `Ctrl+Alt+M` / `Cmd+Alt+M` keyboard shortcut — the keyboard route is load-bearing
 * (a right-click-only affordance is not accessible) and is guarded in
 * `e2e/document-comments.spec.ts` alongside the mouse path. Selecting text captures
 * selection coordinates to position the margin comment composer alongside the anchor.
 *
 * **Undo/redo (BLA-361):** TipTap ships history (StarterKit's `undoRedo`
 * extension, active here — see `tiptapMarkdown.ts:derivativeExtensions`,
 * which does not disable it); this only SURFACES it, it does not build a
 * second undo stack. The affordance itself lives in {@link ./MarkdownEditor}'s
 * existing toolbar row (which is otherwise empty in Rich mode) rather than a
 * second row of editor chrome — BLA-319/ADR-070 deliberately reclaimed the
 * vertical strip above the document body. This file only exposes the
 * capability, via the {@link TipTapEditorHandle} ref: `canUndo`/`canRedo`
 * (`editor.can().undo()/.redo()`, read reactively through `useEditorState`,
 * so the buttons track the live stack and are disabled rather than silently
 * inert) plus `undo()`/`redo()`. `Mod-z`/`Shift-Mod-z`/`Mod-y` are bound by
 * the extension itself, so the buttons and the shortcuts always agree — they
 * drive the exact same command. **Scope: document *content* only** — it
 * undoes/redoes ProseMirror doc transactions (typed text, formatting,
 * inserted/deleted nodes). It does **not** cover comments (a separate
 * `document_comments` sidecar write, ADR-030 — never part of the editor's
 * transaction history) or version restore/load: an out-of-band `value` change
 * REMOUNTS the editor (see {@link TipTapEditor}, the shell at the bottom of
 * this file), and a fresh instance starts with an empty history, so a restore
 * is a hard undo boundary rather than one more undoable step.
 */
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type ReactNodeViewProps,
  useEditor,
  useEditorState,
} from "@tiptap/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";

import { Citation, UnresolvedCitation } from "../blueprint/citationMarker";
import { buildCitationLookup } from "../blueprint/citationResolve";
import { InferenceMarker } from "../blueprint/inferenceMarker";
import { citationLabel, resolveCards } from "../blueprint/inlineCitation";
import type {
  CommentAnchor,
  DocumentComment,
  SourceCitation,
} from "../../types";
import {
  CommentDecoration,
  setCommentDecorations,
  docTextWithMap,
} from "./commentDecoration";
import {
  CitationNode,
  derivativeExtensions,
  InferenceNode,
  readMarkdown,
  UnresolvedCitationNode,
} from "./tiptapMarkdown";

/** The citation provenance a node view needs to label and resolve its marker. */
interface Provenance {
  sourceTitles?: Record<string, string> | null;
  citations?: SourceCitation[] | null;
  cardTitles?: Record<string, string> | null;
}

/**
 * Provenance channel for citation node views.
 *
 * The extension list (and so the node view) is built once at mount, but source
 * titles and citation records arrive asynchronously with the document fetch. A
 * ref would not re-render node views already mounted when they land, leaving
 * the markers stuck on their fallback label; a context does.
 */
const ProvenanceContext = createContext<Provenance>({});

/**
 * Render one citation marker inside the editor.
 *
 * Reuses the same {@link Citation} component and {@link citationLabel} the
 * read-only renderer uses, so a citation looks and behaves identically whether
 * the provider is reading the profile or editing it.
 *
 * @param props - The ProseMirror node view props.
 * @returns The inline marker.
 */
function CitationNodeView(props: ReactNodeViewProps): React.ReactElement {
  const { sourceTitles, citations } = useContext(ProvenanceContext);
  const value = String(props.node.attrs.ref ?? "");
  return (
    <NodeViewWrapper as="span">
      <Citation
        value={value}
        label={citationLabel(value, sourceTitles ?? undefined)}
        citation={buildCitationLookup(citations).get(value)}
      />
    </NodeViewWrapper>
  );
}

/**
 * Render one inference marker inside the editor.
 *
 * Mirrors {@link CitationNodeView}: the same {@link InferenceMarker} the
 * read-only renderer uses, so an interpretation reads identically whether the
 * provider is reading the profile or editing it.
 *
 * @param props - The ProseMirror node view props.
 * @returns The inline marker.
 */
function InferenceNodeView(props: ReactNodeViewProps): React.ReactElement {
  const { cardTitles } = useContext(ProvenanceContext);
  const value = String(props.node.attrs.cards ?? "");
  return (
    <NodeViewWrapper as="span">
      <InferenceMarker cards={resolveCards(value, cardTitles ?? undefined)} />
    </NodeViewWrapper>
  );
}

/**
 * Render one unresolved-citation marker inside the editor.
 *
 * Reuses the same {@link UnresolvedCitation} dagger the read-only renderer uses,
 * so an imported profile's `[[cite:...]]` marker looks and behaves identically
 * whether the provider is reading the profile or editing it. It needs no
 * provenance context — the marker is self-contained (its raw tag text is the
 * only thing it surfaces, kept reachable, never a link).
 *
 * @param props - The ProseMirror node view props.
 * @returns The inline marker.
 */
function UnresolvedCitationNodeView(
  props: ReactNodeViewProps,
): React.ReactElement {
  const value = String(props.node.attrs.tags ?? "");
  return (
    <NodeViewWrapper as="span">
      <UnresolvedCitation tagText={value} />
    </NodeViewWrapper>
  );
}

/**
 * The undo/redo capability {@link TipTapEditor} exposes to its host (BLA-361).
 *
 * The host renders the buttons (in `MarkdownEditor`'s existing toolbar row) but
 * must not own the editor instance, so availability travels as plain booleans
 * and the commands as thunks. A NEW handle object is published whenever
 * `canUndo`/`canRedo` change, so a host holding it in state re-renders and the
 * buttons never go stale.
 */
export interface TipTapEditorHandle {
  /** Whether the history stack has an entry to undo. */
  canUndo: boolean;
  /** Whether the history stack has an entry to redo. */
  canRedo: boolean;
  /** Undo the last document transaction, keeping focus in the editor. */
  undo: () => void;
  /** Redo the last undone document transaction, keeping focus in the editor. */
  redo: () => void;
  /** Formatting triggers */
  toggleBold: () => void;
  toggleItalic: () => void;
  toggleUnderline: () => void;
  toggleStrike: () => void;
  toggleBulletList: () => void;
  toggleOrderedList: () => void;
  indent: () => void;
  outdent: () => void;
  clearFormatting: () => void;
  addCommentOnSelection: () => void;
  setHeading: (level: 1 | 2 | 3 | "p") => void;
  setFontFamily: (font: string) => void;
  setFontSize: (size: number) => void;
  setTextAlign: (align: "left" | "center" | "right") => void;
  setColor: (color: string) => void;
  setHighlight: (color: string) => void;
  insertCitation: (ref: string) => void;
  insertInference: (cards: string) => void;
  /** Active formatting states for ribbon buttons */
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrike: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
  currentHeading: "p" | "h1" | "h2" | "h3";
  currentFontFamily: string;
  currentFontSize: number;
  currentTextAlign: "left" | "center" | "right";
  currentColor: string;
  currentHighlight: string;
}

/** Props for {@link TipTapEditor}. */
interface TipTapEditorProps {
  /** Receives the {@link TipTapEditorHandle} so the host can drive undo/redo. */
  ref?: Ref<TipTapEditorHandle>;
  /** The section key being edited (used for the test id). */
  sectionKey: string;
  /** The current markdown value, used only to seed the editor on mount. */
  value: string;
  /** Whether editing is disabled. */
  disabled?: boolean;
  /** Called with the new markdown whenever the rich editor's content changes. */
  onChange: (markdown: string) => void;
  /** Called when this editor instance gains focus. */
  onFocus?: () => void;
  /** Called when this editor instance loses focus. */
  onBlur?: () => void;
  /** `{ source id: title }` so a marker shows a human source label. */
  sourceTitles?: Record<string, string> | null;
  /** Per-claim provenance, keyed by ref, for the excerpt popover. */
  citations?: SourceCitation[] | null;
  /** `{ card id: name }` so an inference marker can name its cards. */
  cardTitles?: Record<string, string> | null;
  /** Comments to render as inline highlight decorations (BLA-70). */
  comments?: DocumentComment[];
  /** The comment id to render as active (brighter highlight), or null. */
  activeCommentId?: string | null;
  /** The comment id to render as hovered (BLA-318), or null. */
  hoveredCommentId?: string | null;
  /** Called when a comment highlight is clicked. */
  onSelectComment?: (id: string) => void;
  /** Called when a comment highlight is hovered (or unhovered, with `null`). */
  onHoverComment?: (id: string | null) => void;
  /** Called when the provider comments on the current selection. */
  onCreateComment?: (anchor: CommentAnchor, clientY?: number) => void;
}

/** Props for the inner {@link EditorInstance}. */
interface EditorInstanceProps extends TipTapEditorProps {
  /** Reports focus gained/lost, so the shell can guard a live edit. */
  onFocusChange: (focused: boolean) => void;
}

/**
 * One TipTap instance, bound to one document section for its whole lifetime.
 *
 * `value` seeds the editor and is never re-applied: this component is remounted
 * by {@link TipTapEditor} whenever an out-of-band `value` arrives, so "reflect
 * an external change" and "start from a clean slate" are the same operation.
 * That is deliberate — see the shell's own doc comment for why patching the
 * mounted instance instead would leak history across a version boundary.
 *
 * @param props - The section key, seed value, disabled flag, and callbacks.
 * @returns The editor surface.
 */
function EditorInstance({
  ref,
  sectionKey,
  value,
  disabled = false,
  onChange,
  onFocusChange,
  onFocus,
  onBlur,
  sourceTitles,
  citations,
  cardTitles,
  comments,
  activeCommentId = null,
  hoveredCommentId = null,
  onSelectComment,
  onHoverComment,
  onCreateComment,
}: EditorInstanceProps): React.ReactElement {
  // Read the latest callbacks without re-creating the editor.
  const onChangeRef = useRef(onChange);
  const onFocusChangeRef = useRef(onFocusChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const onCreateCommentRef = useRef(onCreateComment);
  useEffect(() => {
    onChangeRef.current = onChange;
    onFocusChangeRef.current = onFocusChange;
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;
    onCreateCommentRef.current = onCreateComment;
  }, [onChange, onFocusChange, onFocus, onBlur, onCreateComment]);

  // Whether a non-empty text selection currently exists (drives the Comment button).
  const [hasSelection, setHasSelection] = useState(false);

  // The selection-native comment menu (Google-Docs-style right-click), positioned
  // at the click point. Null when closed.
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);

  // Close the context menu on an outside click or Escape. A mousedown inside
  // the menu itself is ignored so the subsequent click on its action still
  // fires (a plain "close on any click" would remove the menu — and its
  // button — before the click event reaches it).
  useEffect(() => {
    if (contextMenu === null) return;
    const closeOnOutsideClick = (event: MouseEvent): void => {
      if (contextMenuRef.current?.contains(event.target as Node)) return;
      setContextMenu(null);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setContextMenu(null);
    };
    window.addEventListener("mousedown", closeOnOutsideClick);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("mousedown", closeOnOutsideClick);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [contextMenu]);

  const extensions = useMemo(() => {
    const withViews: Record<string, unknown> = {
      [CitationNode.name]: CitationNode.extend({
        addNodeView: () => ReactNodeViewRenderer(CitationNodeView),
      }),
      [InferenceNode.name]: InferenceNode.extend({
        addNodeView: () => ReactNodeViewRenderer(InferenceNodeView),
      }),
      [UnresolvedCitationNode.name]: UnresolvedCitationNode.extend({
        addNodeView: () => ReactNodeViewRenderer(UnresolvedCitationNodeView),
      }),
    };
    // Replace the bare nodes with their node-view-bearing versions, keeping order.
    const base = derivativeExtensions().map(
      (extension) =>
        (withViews[extension.name] ?? extension) as typeof extension,
    );
    // Append the comment-highlight decoration extension (decoration-only; the
    // click handler lives in the React layer below, so it needs no options).
    return [...base, CommentDecoration];
  }, []);

  const provenance = useMemo(
    () => ({ sourceTitles, citations, cardTitles }),
    [sourceTitles, citations, cardTitles],
  );

  const editor = useEditor(
    {
      extensions,
      content: value,
      editable: !disabled,
      onUpdate: ({ editor: instance }) => {
        onChangeRef.current(readMarkdown(instance));
      },
      onFocus: () => {
        onFocusChangeRef.current(true);
        onFocusRef.current?.();
      },
      onBlur: () => {
        onFocusChangeRef.current(false);
        onBlurRef.current?.();
      },
      onSelectionUpdate: ({ editor: instance }) => {
        const { from, to } = instance.state.selection;
        const hasTextSelection = to > from;
        setHasSelection(hasTextSelection);
        if (!hasTextSelection) {
          setContextMenu(null);
        }
      },
      editorProps: {
        attributes: {
          class:
            "min-h-[180px] p-4 text-[15px] leading-relaxed max-w-none focus:outline-none font-sans",
        },
      },
    },
    [sectionKey],
  );

  // Propagate a `disabled` flip. Guarded on the editor's own current state
  // because `setEditable` emits a synthetic "update" whether or not
  // editability actually changed, and that emit reaches `onUpdate` → `onChange`
  // → the host's autosave, which would mark the document dirty and persist a
  // draft the provider never made. On mount (including the remount after a
  // version restore) `useEditor` has already constructed the instance with the
  // right `editable`, so there is nothing to propagate and this is a no-op.
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable === !disabled) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  // Reactive undo/redo availability (BLA-361): `editor.can()` is a point-in-time
  // read, not itself reactive, so a plain `editor?.can().undo()` read at render
  // time would only ever reflect the stack at the LAST re-render this component
  // happened to get for some other reason — the buttons would go stale. Deriving
  // through `useEditorState` re-runs the selector on every editor transaction,
  // so typing, undoing and redoing all keep the buttons in sync with the real
  // stack. This reports the stack alone; the host owns disabled presentation.
  const editorState = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      canUndo: instance?.can().undo() ?? false,
      canRedo: instance?.can().redo() ?? false,
      isBold: instance?.isActive("bold") ?? false,
      isItalic: instance?.isActive("italic") ?? false,
      isUnderline: instance?.isActive("underline") ?? false,
      isStrike: instance?.isActive("strike") ?? false,
      isBulletList: instance?.isActive("bulletList") ?? false,
      isOrderedList: instance?.isActive("orderedList") ?? false,
      currentHeading: (instance?.isActive("heading", { level: 1 })
        ? "h1"
        : instance?.isActive("heading", { level: 2 })
          ? "h2"
          : instance?.isActive("heading", { level: 3 })
            ? "h3"
            : "p") as "p" | "h1" | "h2" | "h3",
      currentFontFamily:
        (instance?.getAttributes("textStyle").fontFamily as string) || "Inter",
      currentFontSize:
        (instance?.getAttributes("textStyle").fontSize as number) || 11,
      currentTextAlign: (instance?.isActive({ textAlign: "center" })
        ? "center"
        : instance?.isActive({ textAlign: "right" })
          ? "right"
          : "left") as "left" | "center" | "right",
      currentColor:
        (instance?.getAttributes("textStyle").color as string) || "#0f172a",
      currentHighlight:
        (instance?.getAttributes("highlight").color as string) || "transparent",
    }),
  });

  const {
    canUndo,
    canRedo,
    isBold,
    isItalic,
    isUnderline,
    isStrike,
    isBulletList,
    isOrderedList,
    currentHeading,
    currentFontFamily,
    currentFontSize,
    currentTextAlign,
    currentColor,
    currentHighlight,
  } = editorState ?? {
    canUndo: false,
    canRedo: false,
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isStrike: false,
    isBulletList: false,
    isOrderedList: false,
    currentHeading: "p",
    currentFontFamily: "Inter",
    currentFontSize: 11,
    currentTextAlign: "left",
    currentColor: "#0f172a",
    currentHighlight: "transparent",
  };

  const addCommentOnSelection = useCallback((): void => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    if (to <= from) return;
    const quoted = editor.state.doc.textBetween(from, to, " ");
    const prefix = editor.state.doc.textBetween(
      Math.max(0, from - 24),
      from,
      " ",
    );

    const { map } = docTextWithMap(editor.state.doc);
    let char_start = map.indexOf(from);
    if (char_start === -1) {
      char_start = map.findIndex((p) => p >= from);
      if (char_start === -1) char_start = map.length;
    }
    let char_end = map.indexOf(to);
    if (char_end === -1) {
      char_end = map.findIndex((p) => p >= to);
      if (char_end === -1) char_end = map.length;
    }

    let clientY: number | undefined;
    try {
      const coords = editor.view.coordsAtPos(from);
      clientY = coords.top;
    } catch {
      // DOM view may not be available in headless test environments
    }
    if (
      clientY === undefined &&
      typeof window !== "undefined" &&
      window.getSelection
    ) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        if (rect && rect.top > 0) {
          clientY = rect.top;
        }
      }
    }

    onCreateCommentRef.current?.(
      {
        section_key: sectionKey,
        quoted_text: quoted,
        char_start,
        char_end,
        prefix: prefix === "" ? null : prefix,
      },
      clientY,
    );
  }, [editor, sectionKey]);

  // Publish the capability to the host toolbar.
  useImperativeHandle(
    ref,
    () => ({
      canUndo,
      canRedo,
      undo: () => {
        editor?.chain().focus().undo().run();
      },
      redo: () => {
        editor?.chain().focus().redo().run();
      },
      toggleBold: () => {
        editor?.chain().focus().toggleBold().run();
      },
      toggleItalic: () => {
        editor?.chain().focus().toggleItalic().run();
      },
      toggleUnderline: () => {
        editor?.chain().focus().toggleUnderline().run();
      },
      toggleStrike: () => {
        editor?.chain().focus().toggleStrike().run();
      },
      toggleBulletList: () => {
        editor?.chain().focus().toggleBulletList().run();
      },
      toggleOrderedList: () => {
        editor?.chain().focus().toggleOrderedList().run();
      },
      indent: () => {
        editor?.chain().focus().sinkListItem("listItem").run();
      },
      outdent: () => {
        editor?.chain().focus().liftListItem("listItem").run();
      },
      clearFormatting: () => {
        editor?.chain().focus().unsetAllMarks().clearNodes().run();
      },
      addCommentOnSelection: () => {
        addCommentOnSelection();
      },
      setHeading: (level: 1 | 2 | 3 | "p") => {
        if (level === "p") {
          editor?.chain().focus().setParagraph().run();
        } else {
          editor?.chain().focus().setHeading({ level }).run();
        }
      },
      setFontFamily: (font: string) => {
        editor?.chain().focus().setFontFamily(font).run();
      },
      setFontSize: (size: number) => {
        editor?.chain().focus().setMark("textStyle", { fontSize: size }).run();
      },
      setTextAlign: (align: "left" | "center" | "right") => {
        editor?.chain().focus().setTextAlign(align).run();
      },
      setColor: (color: string) => {
        if (color === "inherit") {
          editor?.chain().focus().unsetColor().run();
        } else {
          editor?.chain().focus().setColor(color).run();
        }
      },
      setHighlight: (color: string) => {
        if (color === "transparent") {
          editor?.chain().focus().unsetHighlight().run();
        } else {
          editor?.chain().focus().setHighlight({ color }).run();
        }
      },
      insertCitation: (citationRef: string) => {
        editor
          ?.chain()
          .focus()
          .insertContent({ type: "citation", attrs: { ref: citationRef } })
          .run();
      },
      insertInference: (cards: string) => {
        editor
          ?.chain()
          .focus()
          .insertContent({ type: "inference", attrs: { cards } })
          .run();
      },
      isBold,
      isItalic,
      isUnderline,
      isStrike,
      isBulletList,
      isOrderedList,
      currentHeading,
      currentFontFamily,
      currentFontSize,
      currentTextAlign,
      currentColor,
      currentHighlight,
    }),
    [
      editor,
      canUndo,
      canRedo,
      isBold,
      isItalic,
      isUnderline,
      isStrike,
      isBulletList,
      isOrderedList,
      currentHeading,
      currentFontFamily,
      currentFontSize,
      currentTextAlign,
      currentColor,
      currentHighlight,
      addCommentOnSelection,
    ],
  );

  // Push the comment list into the decoration plugin whenever it changes; the
  // plugin re-locates each anchor against the live doc text on render, so a
  // moved/edited span re-anchors and a deleted one simply stops highlighting.
  useEffect(() => {
    if (!editor) return;
    setCommentDecorations(
      editor,
      comments ?? [],
      activeCommentId,
      hoveredCommentId,
    );
  }, [editor, comments, activeCommentId, hoveredCommentId]);

  // A comment highlight is a plain decoration span carrying data-comment-id;
  // handle its click here (React layer) rather than in the ProseMirror plugin.
  const handleHighlightClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    const el = (event.target as HTMLElement).closest("[data-comment-id]");
    const id = el?.getAttribute("data-comment-id");
    if (id != null) onSelectComment?.(id);
  };

  // Hover parity (BLA-318): hovering a highlight distinguishes its sidebar
  // comment, mirroring the click handler above but additive — clicking still
  // works unchanged, and hover is simply unavailable on touch (no hover
  // event fires there, so the click-only path is never regressed).
  const handleHighlightMouseOver = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    const el = (event.target as HTMLElement).closest("[data-comment-id]");
    const id = el?.getAttribute("data-comment-id");
    onHoverComment?.(id ?? null);
  };

  const handleHighlightMouseOut = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    const related = event.relatedTarget as HTMLElement | null;
    if (related?.closest("[data-comment-id]")) return;
    onHoverComment?.(null);
  };

  // Selection-native comment creation (Google-Docs-style): right-click a
  // selection to open a small menu offering "Comment on selection" instead of
  // reaching for the far-away toolbar button. A right-click with no active
  // selection is left alone (the browser's own context menu, e.g. spellcheck,
  // still opens).
  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!onCreateComment || disabled || !hasSelection) return;
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY });
  };

  // Keyboard-reachable route for the same action (a right-click-only
  // affordance is not accessible): Ctrl+Alt+M / Cmd+Alt+M, mirroring the
  // Google Docs shortcut, comments the current selection without leaving the
  // editor to find a button.
  const handleContainerKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    const isCommentShortcut =
      (event.ctrlKey || event.metaKey) && event.altKey && event.code === "KeyM";
    if (!isCommentShortcut) return;
    if (!onCreateComment || disabled || !hasSelection) return;
    event.preventDefault();
    addCommentOnSelection();
  };

  return (
    <ProvenanceContext.Provider value={provenance}>
      <div
        onClick={handleHighlightClick}
        onMouseOver={handleHighlightMouseOver}
        onMouseOut={handleHighlightMouseOut}
        onContextMenu={handleContextMenu}
        onKeyDown={handleContainerKeyDown}
        role="presentation"
      >
        <EditorContent
          editor={editor}
          data-testid={`rich-editor-${sectionKey}`}
        />
      </div>
      {contextMenu !== null && (
        <div
          ref={contextMenuRef}
          data-testid={`comment-context-menu-${sectionKey}`}
          role="menu"
          style={{ position: "fixed", top: contextMenu.y, left: contextMenu.x }}
          className="z-50 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            data-testid={`comment-context-menu-item-${sectionKey}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              addCommentOnSelection();
              setContextMenu(null);
            }}
            className="block w-full whitespace-nowrap px-3 py-1.5 text-left text-xs font-medium text-slate-700 hover:bg-indigo-50"
          >
            Comment on selection
          </button>
        </div>
      )}
    </ProvenanceContext.Provider>
  );
}

/**
 * Mount a TipTap editor bound to one document section.
 *
 * This shell holds no editor state of its own; it exists to decide WHEN the
 * instance below it is thrown away and rebuilt. `value` seeds the editor, and
 * the editor's own edits flow value → onChange → parent → value, so those must
 * not re-seed it and fight the user's cursor. An *out-of-band* `value` change —
 * one the editor did not originate, e.g. loading or restoring a prior version —
 * must be reflected, or the pane silently shows stale content (the
 * version-restore no-op bug, ADR-027/BLA-179).
 *
 * That external change is applied by REMOUNTING rather than by patching the
 * live instance with `setContent`, because `setContent` does not clear
 * prosemirror-history: it keeps the pre-restore entries AND records the
 * replacement as one more undoable step, so a provider could undo straight back
 * across a version restore and let autosave persist the superseded body over
 * the version they just restored. TipTap exposes no history-reset command, so a
 * fresh instance — which starts with an empty history — is the mechanism, and a
 * restore/load becomes a hard undo boundary.
 *
 * The remount is driven by a React `key` (not by `useEditor`'s own dependency
 * array) so the whole hook scope is recreated with it. Swapping only the
 * instance leaves `useEditorState`'s cached snapshot pointing at the destroyed
 * editor, which it then hands to the selector during the next render —
 * `can()` dereferences a nulled command manager and throws.
 *
 * The decision is made synchronously in render (the guarded
 * setState-during-render pattern), which re-invokes this component in the SAME
 * render pass — so the new instance is rendered with both the bumped key and
 * the incoming `value`, constructed during that render (`immediatelyRender`
 * defaults true here) and attached to the DOM in the commit that follows it.
 * The pane therefore never paints the superseded body.
 *
 * @param props - The section key, current value, disabled flag, and callbacks.
 * @returns The editor surface.
 */
export function TipTapEditor({
  ref,
  value,
  onChange,
  onFocus,
  onBlur,
  ...rest
}: TipTapEditorProps): React.ReactElement {
  // Whether the instance below currently holds focus, mirrored off TipTap's own
  // focus/blur callbacks — the decision below runs before that instance exists.
  const [isFocused, setIsFocused] = useState(false);

  // `value` as this shell last saw it, plus the key of the instance showing it.
  // `value` advances both when the editor emits an edit (so the round trip back
  // through the parent is recognised as ours by VALUE, never inferred from
  // focus — an undo/redo toolbar click is a real in-editor edit made while
  // focus may sit on the button) and when an external change is adopted.
  const [mounted, setMounted] = useState({ key: 0, value });

  if (value !== mounted.value) {
    // A focused editor means the user is mid-edit and their own onChange
    // already carries their text, so we never remount under a live selection.
    setMounted((prev) => ({
      key: isFocused ? prev.key : prev.key + 1,
      value,
    }));
  }

  const handleChange = (markdown: string): void => {
    setMounted((prev) => ({ ...prev, value: markdown }));
    onChange(markdown);
  };

  return (
    <EditorInstance
      key={mounted.key}
      ref={ref}
      value={value}
      onChange={handleChange}
      onFocusChange={setIsFocused}
      onFocus={onFocus}
      onBlur={onBlur}
      {...rest}
    />
  );
}

export default TipTapEditor;
